import { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  FormControlLabel,
  Checkbox,
} from '@mui/material';

import Card from '@/components/Card';
import Stack from '@/components/Stack';
import { Body, Small } from '@/components/Typography';
import Badge from '@/components/Badge';
import { axiosRubyClient } from '@/util/axios/axiosClient';
import { ConsolidatedSkill } from '@/services/workersV2API';

type Props = {
  skill: ConsolidatedSkill;
  workerId: number;
  onUpdate: () => void;
};

const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return '--';
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const formatDateUTC = (dateString: string | null | undefined): string => {
  if (!dateString) return '--';
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
};

const BooleanBadge = ({ value }: { value: boolean }) => {
  return value ? (
    <Badge label="Yes" status="theme" />
  ) : (
    <Badge label="No" status="danger" />
  );
};

const KeyValueRow = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <TableRow>
    <TableCell
      component="th"
      scope="row"
      sx={{
        fontWeight: 600,
        width: '40%',
        color: '#6B7280',
        fontSize: '13px',
        borderBottom: '1px solid #F3F4F6',
        padding: '10px 16px',
      }}
    >
      {label}
    </TableCell>
    <TableCell
      sx={{
        fontSize: '13px',
        borderBottom: '1px solid #F3F4F6',
        padding: '10px 16px',
      }}
    >
      {children}
    </TableCell>
  </TableRow>
);

const SkillOverviewTab = ({ skill, workerId, onUpdate }: Props) => {
  const [loadingAction, setLoadingAction] = useState<string | null>(null);
  const [expiryDialog, setExpiryDialog] = useState<{
    open: boolean;
    value: string;
  }>({
    open: false,
    value: '',
  });
  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean;
    markAsIneligible: boolean;
  }>({
    open: false,
    markAsIneligible: false,
  });

  const workerSkill = skill.worker_skill;
  const skillRequest = skill.skill_request;

  const handleReject = async () => {
    if (!skillRequest) return;
    setLoadingAction('reject');
    try {
      const response = await axiosRubyClient.patch(
        `/api/admin/skill_requests/${skillRequest.id}/reject`,
        { rejected: true },
      );
      if (response.data.success) {
        onUpdate();
      }
    } catch (error) {
      console.error('Error rejecting skill request:', error);
    } finally {
      setLoadingAction(null);
    }
  };

  const handleUnreject = async () => {
    if (!skillRequest) return;
    setLoadingAction('unreject');
    try {
      const response = await axiosRubyClient.patch(
        `/api/admin/skill_requests/${skillRequest.id}/reject`,
        { rejected: false },
      );
      if (response.data.success) {
        onUpdate();
      }
    } catch (error) {
      console.error('Error unrejecting skill request:', error);
    } finally {
      setLoadingAction(null);
    }
  };

  const handleAccept = async () => {
    if (!skillRequest) return;
    setLoadingAction('accept');
    try {
      const response = await axiosRubyClient.patch(
        `/api/admin/skill_requests/${skillRequest.id}/accept`,
      );
      if (response.data.success) {
        onUpdate();
      }
    } catch (error) {
      console.error('Error accepting skill request:', error);
    } finally {
      setLoadingAction(null);
    }
  };

  const handleToggle = async () => {
    if (!workerSkill) return;
    setLoadingAction('toggle');
    try {
      const response = await axiosRubyClient.post(
        `/api/admin/worker_skills/${workerSkill.id}/toggle`,
      );
      if (response.data.success) {
        onUpdate();
      }
    } catch (error) {
      console.error('Error toggling worker skill:', error);
    } finally {
      setLoadingAction(null);
    }
  };

  const handleSetExpiry = async () => {
    if (!workerSkill || !expiryDialog.value) return;
    setLoadingAction('expiry');
    try {
      const response = await axiosRubyClient.patch(
        `/api/admin/worker_skills/${workerSkill.id}`,
        { expires_at: `${expiryDialog.value}T14:00:00Z` },
      );
      if (response.data.success) {
        setExpiryDialog({ open: false, value: '' });
        onUpdate();
      }
    } catch (error) {
      console.error('Error setting expiry:', error);
    } finally {
      setLoadingAction(null);
    }
  };

  const handleDeleteSkill = async () => {
    if (!workerSkill) return;
    setLoadingAction('delete');
    try {
      const response = await axiosRubyClient.delete(
        `/api/admin/worker_skills/${workerSkill.id}`,
        {
          data: {
            mark_as_ineligible: deleteDialog.markAsIneligible,
          },
        },
      );
      if (response.data.success) {
        setDeleteDialog({ open: false, markAsIneligible: false });
        onUpdate();
      }
    } catch (error) {
      console.error('Error removing skill:', error);
    } finally {
      setLoadingAction(null);
    }
  };

  const handleCreateWorkerSkill = async () => {
    setLoadingAction('create_ws');
    try {
      const response = await axiosRubyClient.post(
        `/api/admin/workers/${workerId}/worker_skills`,
        { skill_id: skill.skill_id },
      );
      if (response.data.success) {
        onUpdate();
      }
    } catch (error) {
      console.error('Error creating worker skill:', error);
    } finally {
      setLoadingAction(null);
    }
  };

  const getSkillRequestStatusBadge = () => {
    if (!skillRequest) return null;
    if (skillRequest.accepted_at)
      return <Badge label="Accepted" status="theme" />;
    if (skillRequest.rejected_at)
      return <Badge label="Rejected" status="danger" />;
    return <Badge label="Pending" status="warning" />;
  };

  const isRejected = skillRequest?.rejected_at != null;
  const isAccepted = skillRequest?.accepted_at != null;
  const isPending = skillRequest != null && !isAccepted && !isRejected;

  return (
    <Stack vertical gap={24} style={{ width: '100%' }}>
      {/* Record Status */}
      <Card>
        <Card.Section>
          <Stack gap={16} wrap>
            <Stack gap={8} style={{ alignItems: 'center' }}>
              <div
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: workerSkill ? '#10B981' : '#EF4444',
                }}
              />
              <Small>
                Worker Skill:{' '}
                {workerSkill ? `Exists (ID: ${workerSkill.id})` : 'Not created'}
              </Small>
            </Stack>
            <Stack gap={8} style={{ alignItems: 'center' }}>
              <div
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: skillRequest ? '#10B981' : '#9CA3AF',
                }}
              />
              <Small>
                Skill Request:{' '}
                {skillRequest ? `Exists (ID: ${skillRequest.id})` : 'None'}
              </Small>
            </Stack>
          </Stack>
        </Card.Section>
      </Card>

      {/* Worker Skill */}
      <Card
        title="Worker Skill"
        actionsDropdown={
          workerSkill ? (
            <Stack gap={8}>
              {!workerSkill.disabled && (
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  disabled={loadingAction !== null}
                  onClick={handleToggle}
                  startIcon={
                    loadingAction === 'toggle' ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : undefined
                  }
                >
                  {loadingAction === 'toggle' ? 'Disabling...' : 'Disable'}
                </Button>
              )}
              <Button
                variant="outlined"
                size="small"
                disabled={loadingAction !== null}
                onClick={() =>
                  setExpiryDialog({
                    open: true,
                    value: workerSkill.expires_at
                      ? workerSkill.expires_at.split('T')[0]
                      : '',
                  })
                }
              >
                Set Expiry
              </Button>
            </Stack>
          ) : (
            <Stack gap={8}>
              <Button
                variant="contained"
                size="small"
                disabled={loadingAction !== null}
                onClick={handleCreateWorkerSkill}
                startIcon={
                  loadingAction === 'create_ws' ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : undefined
                }
              >
                {loadingAction === 'create_ws'
                  ? 'Creating...'
                  : 'Create Worker Skill'}
              </Button>
            </Stack>
          )
        }
      >
        <Card.Section>
          {workerSkill ? (
            <>
              {workerSkill.disabled && (
                <Stack
                  gap={12}
                  style={{
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '12px 16px',
                    marginBottom: '12px',
                    backgroundColor: '#FEF2F2',
                    borderRadius: '8px',
                    border: '1px solid #FECACA',
                  }}
                >
                  <Stack gap={8} style={{ alignItems: 'center' }}>
                    <Badge label="Disabled" status="danger" />
                    <Small>This worker skill is currently disabled.</Small>
                  </Stack>
                  <Button
                    variant="contained"
                    color="success"
                    size="small"
                    disabled={loadingAction !== null}
                    onClick={handleToggle}
                    startIcon={
                      loadingAction === 'toggle' ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : undefined
                    }
                  >
                    {loadingAction === 'toggle' ? 'Enabling...' : 'Enable'}
                  </Button>
                </Stack>
              )}
              <Table size="small">
                <TableBody>
                  <KeyValueRow label="ID">{workerSkill.id}</KeyValueRow>
                  <KeyValueRow label="Expires At">
                    {formatDateUTC(workerSkill.expires_at)}
                  </KeyValueRow>
                  <KeyValueRow label="Renewal Notified At">
                    {formatDate(workerSkill.renewal_notified_at)}
                  </KeyValueRow>
                  <KeyValueRow label="Days Until Expiration">
                    {workerSkill.days_until_expiration != null
                      ? workerSkill.days_until_expiration
                      : '--'}
                  </KeyValueRow>
                  <KeyValueRow label="Jobs Count">
                    {workerSkill.jobs_count}
                  </KeyValueRow>
                  <KeyValueRow label="Jobs Worked">
                    {workerSkill.jobs_worked}
                  </KeyValueRow>
                  <KeyValueRow label="Job Rating Sum">
                    {workerSkill.job_rating_sum}
                  </KeyValueRow>
                  <KeyValueRow label="Created At">
                    {formatDate(workerSkill.created_at)}
                  </KeyValueRow>
                  <KeyValueRow label="Updated At">
                    {formatDate(workerSkill.updated_at)}
                  </KeyValueRow>
                </TableBody>
              </Table>
            </>
          ) : (
            <Small color="lighter">No worker skill record exists yet.</Small>
          )}
        </Card.Section>
      </Card>

      {/* Skill Request */}
      {skillRequest && (
        <Card
          title="Skill Request"
          actionsDropdown={
            <Stack gap={8}>
              {isPending && (
                <Button
                  variant="contained"
                  color="success"
                  size="small"
                  disabled={loadingAction !== null}
                  onClick={handleAccept}
                  startIcon={
                    loadingAction === 'accept' ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : undefined
                  }
                >
                  {loadingAction === 'accept' ? 'Approving...' : 'Approve'}
                </Button>
              )}
              {!isRejected && (
                <Button
                  variant="contained"
                  color="error"
                  size="small"
                  disabled={loadingAction !== null}
                  onClick={handleReject}
                  startIcon={
                    loadingAction === 'reject' ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : undefined
                  }
                >
                  {loadingAction === 'reject' ? 'Rejecting...' : 'Reject'}
                </Button>
              )}
              {isRejected && (
                <Button
                  variant="outlined"
                  size="small"
                  disabled={loadingAction !== null}
                  onClick={handleUnreject}
                  startIcon={
                    loadingAction === 'unreject' ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : undefined
                  }
                >
                  {loadingAction === 'unreject' ? 'Unrejecting...' : 'Unreject'}
                </Button>
              )}
            </Stack>
          }
        >
          <Card.Section>
            <Table size="small">
              <TableBody>
                <KeyValueRow label="ID">{skillRequest.id}</KeyValueRow>
                <KeyValueRow label="Status">
                  {getSkillRequestStatusBadge()}
                </KeyValueRow>
                <KeyValueRow label="Work Experience Completed">
                  <Stack gap={8} style={{ alignItems: 'center' }}>
                    <BooleanBadge
                      value={skillRequest.work_experience_completed}
                    />
                    {skillRequest.work_experience_completed_at && (
                      <Small color="lighter">
                        {formatDate(skillRequest.work_experience_completed_at)}
                      </Small>
                    )}
                  </Stack>
                </KeyValueRow>
                <KeyValueRow label="Assessment Passed">
                  <Stack gap={8} style={{ alignItems: 'center' }}>
                    <BooleanBadge value={skillRequest.assessment_passed} />
                    {skillRequest.assessment_passed_at && (
                      <Small color="lighter">
                        {formatDate(skillRequest.assessment_passed_at)}
                      </Small>
                    )}
                  </Stack>
                </KeyValueRow>
                <KeyValueRow label="Uniform Photo Uploaded">
                  <Stack gap={8} style={{ alignItems: 'center' }}>
                    <BooleanBadge value={skillRequest.uniform_photo_uploaded} />
                    {skillRequest.uniform_photo_uploaded_at && (
                      <Small color="lighter">
                        {formatDate(skillRequest.uniform_photo_uploaded_at)}
                      </Small>
                    )}
                  </Stack>
                </KeyValueRow>
                <KeyValueRow label="Certificate Photo Uploaded">
                  <Stack gap={8} style={{ alignItems: 'center' }}>
                    <BooleanBadge
                      value={skillRequest.certificate_photo_uploaded}
                    />
                    {skillRequest.certificate_photo_uploaded_at && (
                      <Small color="lighter">
                        {formatDate(skillRequest.certificate_photo_uploaded_at)}
                      </Small>
                    )}
                  </Stack>
                </KeyValueRow>
                <KeyValueRow label="Accepted At">
                  {formatDate(skillRequest.accepted_at)}
                </KeyValueRow>
                <KeyValueRow label="Rejected At">
                  {formatDate(skillRequest.rejected_at)}
                </KeyValueRow>
                <KeyValueRow label="Rejection Comment">
                  {skillRequest.rejection_comment || '--'}
                </KeyValueRow>
                <KeyValueRow label="Assessment Attempt Count">
                  {skillRequest.assessment_attempt_count}
                </KeyValueRow>
                <KeyValueRow label="Assessment Permanently Ineligible">
                  <BooleanBadge
                    value={skillRequest.assessment_permanently_ineligible}
                  />
                </KeyValueRow>
                <KeyValueRow label="Can Retry Assessment">
                  <BooleanBadge value={skillRequest.can_retry_assessment} />
                </KeyValueRow>
                <KeyValueRow label="Retry Eligible At">
                  {formatDate(skillRequest.assessment_retry_eligible_at)}
                </KeyValueRow>
                <KeyValueRow label="Retry Message">
                  {skillRequest.retry_message || '--'}
                </KeyValueRow>
                <KeyValueRow label="All Requirements Completed">
                  <BooleanBadge
                    value={skillRequest.all_requirements_completed}
                  />
                </KeyValueRow>
                <KeyValueRow label="Completion Percentage">
                  {skillRequest.completion_percentage}%
                </KeyValueRow>
                <KeyValueRow label="Created At">
                  {formatDate(skill.created_at)}
                </KeyValueRow>
              </TableBody>
            </Table>
          </Card.Section>
        </Card>
      )}

      {/* Danger Zone */}
      {workerSkill && (
        <Card>
          <Card.Section>
            <Stack
              style={{
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '16px',
              }}
            >
              <Stack gap={8} style={{ alignItems: 'center' }}>
                <div
                  style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: '#EF4444',
                  }}
                />
                <Body weight="semibold">Danger Zone</Body>
              </Stack>
            </Stack>

            <Stack vertical gap={12}>
              <Small color="lighter">
                Removing this skill will prevent the worker from being assigned
                to jobs requiring this skill.
              </Small>
              <Button
                variant="outlined"
                size="small"
                color="error"
                disabled={loadingAction !== null}
                onClick={() =>
                  setDeleteDialog({ open: true, markAsIneligible: false })
                }
              >
                {loadingAction === 'delete'
                  ? 'Removing...'
                  : 'Remove Skill from Worker'}
              </Button>
            </Stack>
          </Card.Section>
        </Card>
      )}

      {/* Set Expiry Dialog */}
      <Dialog
        open={expiryDialog.open}
        onClose={() => setExpiryDialog({ open: false, value: '' })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Set Expiry Date</DialogTitle>
        <DialogContent>
          <TextField
            type="date"
            fullWidth
            value={expiryDialog.value}
            onChange={(e) =>
              setExpiryDialog({ ...expiryDialog, value: e.target.value })
            }
            sx={{ marginTop: '8px' }}
            InputLabelProps={{ shrink: true }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExpiryDialog({ open: false, value: '' })}>
            Cancel
          </Button>
          <Button
            onClick={handleSetExpiry}
            variant="contained"
            color="primary"
            disabled={!expiryDialog.value || loadingAction === 'expiry'}
            startIcon={
              loadingAction === 'expiry' ? (
                <CircularProgress size={16} color="inherit" />
              ) : undefined
            }
          >
            {loadingAction === 'expiry' ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Skill Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() =>
          setDeleteDialog({ open: false, markAsIneligible: false })
        }
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Remove Skill from Worker</DialogTitle>
        <DialogContent>
          <Small style={{ marginBottom: '16px', display: 'block' }}>
            Are you sure you want to remove "{skill.skill_name}" from this
            worker? This action cannot be undone.
          </Small>
          <FormControlLabel
            control={
              <Checkbox
                checked={deleteDialog.markAsIneligible}
                onChange={(e) =>
                  setDeleteDialog({
                    ...deleteDialog,
                    markAsIneligible: e.target.checked,
                  })
                }
                color="primary"
              />
            }
            label="Also mark as ineligible"
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              setDeleteDialog({ open: false, markAsIneligible: false })
            }
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteSkill}
            variant="contained"
            color="error"
            disabled={loadingAction === 'delete'}
            startIcon={
              loadingAction === 'delete' ? (
                <CircularProgress size={16} color="inherit" />
              ) : undefined
            }
          >
            {loadingAction === 'delete' ? 'Removing...' : 'Remove Skill'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default SkillOverviewTab;
